import { useEffect, useState } from "react";
import { apiPatch } from "@/lib/apiClient";
import {
  CRM_ACCOUNT_STATUSES,
  CRM_LEAD_SOURCES,
  formatCrmSourceLabel,
  formatCrmStatusLabel,
  normalizeCrmSourceForSave,
} from "@/lib/crmDisplay";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACCOUNT_STAGES = [
  "new",
  "qualified",
  "meeting_booked",
  "proposal_sent",
  "negotiation",
  "closed_won",
  "closed_lost",
] as const;

export type AccountEditSnapshot = {
  name: string;
  domain: string | null;
  website: string | null;
  email: string | null;
  companyPhone: string | null;
  industry: string | null;
  city: string | null;
  state: string | null;
  status: string;
  stage: string;
  score: number;
  source: string;
  ownerId: string | null;
};

type CrmAccountEditDialogProps = {
  accountId: string;
  initial: AccountEditSnapshot;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (account: AccountEditSnapshot & { id?: string }) => void;
};

export function CrmAccountEditDialog({
  accountId,
  initial,
  open,
  onOpenChange,
  onSaved,
}: CrmAccountEditDialogProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(initial.name);
  const [domain, setDomain] = useState(initial.domain || "");
  const [website, setWebsite] = useState(initial.website || "");
  const [email, setEmail] = useState(initial.email || "");
  const [companyPhone, setCompanyPhone] = useState(initial.companyPhone || "");
  const [industry, setIndustry] = useState(initial.industry || "");
  const [city, setCity] = useState(initial.city || "");
  const [stateVal, setStateVal] = useState(initial.state || "");
  const [status, setStatus] = useState(initial.status);
  const [stage, setStage] = useState(initial.stage);
  const [score, setScore] = useState(String(initial.score ?? 0));
  const [source, setSource] = useState(formatCrmSourceLabel(initial.source) || "Manual");
  const [ownerId, setOwnerId] = useState(initial.ownerId || "");

  useEffect(() => {
    if (!open) return;
    setName(initial.name);
    setDomain(initial.domain || "");
    setWebsite(initial.website || "");
    setEmail(initial.email || "");
    setCompanyPhone(initial.companyPhone || "");
    setIndustry(initial.industry || "");
    setCity(initial.city || "");
    setStateVal(initial.state || "");
    setStatus(initial.status);
    setStage(initial.stage);
    setScore(String(initial.score ?? 0));
    setSource(formatCrmSourceLabel(initial.source) || "Manual");
    setOwnerId(initial.ownerId || "");
    setError(null);
  }, [open, initial]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const scoreNum = Number(score);
    if (Number.isNaN(scoreNum)) {
      setError("Score must be a number.");
      setSaving(false);
      return;
    }
    const res = await apiPatch<AccountEditSnapshot & { id: string }>(
      `/api/crm/accounts/${encodeURIComponent(accountId)}`,
      {
        name: name.trim(),
        domain: domain.trim() === "" ? null : domain.trim(),
        website: website.trim() === "" ? null : website.trim(),
        email: email.trim() === "" ? null : email.trim(),
        companyPhone: companyPhone.trim() === "" ? null : companyPhone.trim(),
        industry: industry.trim() === "" ? null : industry.trim(),
        city: city.trim() === "" ? null : city.trim(),
        state: stateVal.trim() === "" ? null : stateVal.trim(),
        status,
        stage,
        score: scoreNum,
        source: normalizeCrmSourceForSave(source),
        ownerId: ownerId.trim() === "" ? null : ownerId.trim(),
      },
    );
    setSaving(false);
    if (!res.ok || !res.data) {
      setError(res.error || "Unable to save account.");
      return;
    }
    onOpenChange(false);
    onSaved(res.data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit account</DialogTitle>
        </DialogHeader>
        {error ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{error}</div>
        ) : null}
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-sm md:col-span-2">
            <span className="font-medium">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Domain</span>
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Website</span>
            <input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Email</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Phone</span>
            <input
              value={companyPhone}
              onChange={(e) => setCompanyPhone(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Industry</span>
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">City</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">State</span>
            <input
              value={stateVal}
              onChange={(e) => setStateVal(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            >
              {CRM_ACCOUNT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {formatCrmStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Stage</span>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            >
              {ACCOUNT_STAGES.map((s) => (
                <option key={s} value={s}>
                  {formatCrmStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Score</span>
            <input
              type="number"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">Source</span>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            >
              {CRM_LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm md:col-span-2">
            <span className="font-medium">Owner ID</span>
            <input
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save account"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
